// conditions.ts
import { uniq } from "lodash"
import {
  FieldCondition,
  FieldAttributes,
  SectionData,
  ConditionSet,
  DateCondition,
} from "types"
import { isEmptyEquals, getValueOrNestedValue } from "./helpers"

export type FormValues = Record<
  string,
  string | number | boolean | null | undefined | Array<string>
>
/**
 * Determine if a field should be shown or hidden. By default, it should be shown
 *
 * @param fieldData The JSON representing the field
 */
export const determineShowField = (
  fieldData: FieldAttributes | SectionData,
  formValues?: FormValues,
  nestedIndex?: number,
  isFinalCheck?: boolean,
  isMultipleSection?: boolean,
): boolean => {
  // Check for boolean truthiness first
  if (fieldData.show !== undefined && typeof fieldData.show === "boolean") {
    return fieldData.show
  }

  if (fieldData.hide !== undefined && typeof fieldData.hide === "boolean") {
    return !fieldData.hide
  }

  const showConditionSet = fieldData.show as ConditionSet
  const hideConditionSet = fieldData.hide as ConditionSet

  // Check condition set truthiness
  if (hideConditionSet?.conditions?.length > 0) {
    // If the hide condition set evaluates to true, then the field should be hidden (return false)
    return !isConditionSetMet(
      hideConditionSet,
      formValues,
      nestedIndex,
      isFinalCheck,
      isMultipleSection,
    )
  }
  if (showConditionSet?.conditions?.length > 0) {
    // If the show condition set evaluates to true, the the field should be shown (return true)
    return isConditionSetMet(
      showConditionSet,
      formValues,
      nestedIndex,
      isFinalCheck,
      isMultipleSection,
    )
  }
  // Show the field by default
  return true
}

export const determineIsRequired = (
  fieldData: FieldAttributes,
  formValues?: FormValues,
  nestedIndex?: number,
  isFinalCheck?: boolean,
  isMultipleSection?: boolean,
): boolean => {
  if (fieldData.isRequired === undefined) {
    return false
  }

  if (typeof fieldData.isRequired === "boolean") {
    return fieldData.isRequired
  }

  const requiredConditionSet = fieldData.isRequired
  if (requiredConditionSet.conditions?.length > 0) {
    // If the required condition set evaluates to true, the the field should be required (return true)
    return isConditionSetMet(
      requiredConditionSet,
      formValues,
      nestedIndex,
      isFinalCheck,
      isMultipleSection,
    )
  }
  // Show the field by default
  return false
}

export const determineShouldPrefill = (
  fieldData: FieldAttributes,
  hasPrefilled: boolean,
  formValues?: FormValues,
  nestedIndex?: number,
): { shouldPrefill: boolean; prefillValue: any; shouldClear: boolean } => {
  // Prefill a value if all conditions are met
  // Conditions:
  // 1. The field has a prefill
  // 2. The current value is empty (null/undefined) and you haven't already prefilled OR override is true
  // 3. The current value is not already equal to the override
  // 4. The prefill conditionSet is met
  const prefills = fieldData.prefill

  if (!prefills || !formValues) {
    return { shouldPrefill: false, prefillValue: null, shouldClear: false }
  }

  const fieldValue = formValues[fieldData.name]
  
  const fieldValueStr = fieldValue?.toString()
  const fieldHasNoValue =
    [null, undefined, ""].includes(
      fieldValueStr as string | null | undefined
    ) && !hasPrefilled

  const prefillContainsOverride = prefills.some(p => p.override)

  const valueMayBeUpdated = fieldHasNoValue || prefillContainsOverride

  const possibleValues = prefills.map(p => getValueOrNestedValue(p.value))

  const hasNewValue = !possibleValues.every(v => v === fieldValue)

  const fieldConditions = prefills.map(p =>
    isConditionSetMet(p.conditionSet, formValues, nestedIndex),
  )

  const hasMetConditions = fieldConditions.some(c => c === true)

  const shouldPrefill = valueMayBeUpdated && hasNewValue && hasMetConditions

  const shouldClear = !fieldHasNoValue && !hasMetConditions

  return {
    shouldPrefill: !!shouldPrefill,
    prefillValue: shouldPrefill ? prefills[0].value : null,
    shouldClear,
  }
}

export const isConditionSetMet = (
  conditionSet?: ConditionSet,
  formValues?: FormValues,
  nestedIndex?: number,
  isFinalCheck?: boolean,
  isMultipleSection?: boolean,
) => {
  if (!conditionSet) {
    return true
  }

  const conditionsTruthiness = conditionSet.conditions.map(condition => {
    return isConditionMet(
      condition,
      formValues || {},
      nestedIndex,
      isFinalCheck,
      isMultipleSection,
    )
  })

  return parseLogicFromTruthValues(
    conditionsTruthiness,
    conditionSet.logic || "",
    conditionSet.logicalOr || false,
  )
}

export const isConditionMet = (
  condition: FieldCondition,
  formValues: { [key: string]: any },
  nestedIndex?: number,
  isFinalCheck?: boolean,
  isMultipleSection?: boolean,
): boolean => {
  let fieldName = condition.name

  if (nestedIndex !== undefined) {
    // If there is a nested index, the field you're looking for uses index instead of {{x}}
    fieldName =
      isFinalCheck && isMultipleSection
        ? fieldName.replace("_", `_${nestedIndex + 1}_`)
        : fieldName
  }

  const formValue = formValues[fieldName] ?? null

  // Checkboxes return an array of values
  const isArrayIncludesCondition =
    Array.isArray(formValue) &&
    formValue.includes(condition.value || condition.notValue)

  const currentValue = getValueOrNestedValue(formValue)

  if (condition.date) {
    return isDateConditionMet(condition.date, currentValue)
  }

  if (condition.value !== undefined) {
    if (isArrayIncludesCondition) {
      return true
    }

    return isEmptyEquals(currentValue, condition.value)
  }

  if (condition.notValue !== undefined) {
    if (isArrayIncludesCondition) {
      return false
    }
    // In a notValue scenario, empty could mean "" or null or undefined
    return !isEmptyEquals(currentValue, condition.notValue)
  }

  // No conditions to test
  return true
}

export const isDateConditionMet = (
  dateCondition: DateCondition,
  currentValue: string,
): boolean => {
  if (
    currentValue === null ||
    currentValue === undefined ||
    currentValue === ""
  ) {
    return false
  }

  const parseDate = (dateString: string): Date | null => {
    // First, try parsing as-is
    let date = new Date(dateString)
    if (!isNaN(date.getTime())) {
      return date
    }

    // If that fails, try to parse common date formats
    const formats = [
      /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/, // DD-MM-YYYY or MM-DD-YYYY
      /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/, // YYYY-MM-DD
    ]

    for (const format of formats) {
      const match = format.exec(dateString)
      if (match) {
        // Assume American format (MM-DD-YYYY) if day and month are ambiguous
        const [, first, second, year] = match
        date = new Date(
          `${year}-${first.padStart(2, "0")}-${second.padStart(2, "0")}`,
        )
        if (!isNaN(date.getTime())) {
          return date
        }
      }
    }
    // If all parsing attempts fail, return null
    return null
  }

  const currentDate = parseDate(currentValue)

  if (currentDate === null) {
    // Only warn if the input looks like it should be a complete date
    if (currentValue.replace(/\D/g, "").length >= 8) {
      console.warn("Unable to parse date input:", currentValue)
    }
    return false
  }

  if (dateCondition.before) {
    const beforeDate = parseDate(dateCondition.before)
    if (beforeDate === null) {
      console.warn("Invalid before date:", dateCondition.before)
      return false
    }
    return currentDate < beforeDate
  }

  if (dateCondition.after) {
    const afterDate = parseDate(dateCondition.after)
    if (afterDate === null) {
      console.warn("Invalid after date:", dateCondition.after)
      return false
    }
    return currentDate > afterDate
  }

  if (dateCondition.equals) {
    const equalsDate = parseDate(dateCondition.equals)
    if (equalsDate === null) {
      console.warn("Invalid equals date:", dateCondition.equals)
      return false
    }
    return currentDate.getTime() === equalsDate.getTime()
  }

  return false
}

export const getConditionalFieldsToWatch = (
  data: FieldAttributes | SectionData,
  name?: string,
  nestedIndex?: number,
): string[] => {
  // Always watch your own field
  let fieldsToWatch = name ? [name] : []

  if (data.hide !== undefined && typeof data.hide !== "boolean") {
    const hideConditionSet = data.hide

    fieldsToWatch = [
      ...fieldsToWatch,
      ...hideConditionSet.conditions.map(condition =>
        condition.name.replaceAll(
          "{{x}}",
          nestedIndex !== undefined ? `${nestedIndex + 1}` : "{{x}}",
        ),
      ),
    ]
  }
  if (data.show !== undefined && typeof data.show !== "boolean") {
    const showConditionSet = data.show
    fieldsToWatch = [
      ...fieldsToWatch,
      ...showConditionSet.conditions.map(condition =>
        condition.name.replaceAll(
          "{{x}}",
          nestedIndex !== undefined ? `${nestedIndex + 1}` : "{{x}}",
        ),
      ),
    ]
  }

  // Type cast to check for required field
  // @ts-ignore
  if (data.isRequired !== undefined) {
    const dataField = data as FieldAttributes
    if (
      dataField.isRequired !== undefined &&
      typeof dataField.isRequired !== "boolean"
    ) {
      const requiredConditions = dataField.isRequired
      fieldsToWatch = [
        ...fieldsToWatch,
        ...requiredConditions.conditions.map(condition =>
          condition.name.replaceAll(
            "{{x}}",
            nestedIndex !== undefined ? `${nestedIndex + 1}` : "{{x}}",
          ),
        ),
      ]
    }
  }

  // Look for fields that the prefill value might depend on
  // @ts-ignore
  if (data.prefill !== undefined) {
    const dataField = data as FieldAttributes
    if (dataField.prefill?.[0]?.conditionSet) {
      const requiredConditions = dataField.prefill[0].conditionSet
      fieldsToWatch = [
        ...fieldsToWatch,
        ...requiredConditions.conditions.map(condition =>
          condition.name.replaceAll(
            "{{x}}",
            nestedIndex !== undefined ? `${nestedIndex + 1}` : "{{x}}",
          ),
        ),
      ]
    }
  }

  return uniq(fieldsToWatch)
}

export const parseLogicFromTruthValues = (
  truthValues: boolean[],
  logic: string,
  logicalOr?: boolean,
): boolean => {
  if (truthValues.length === 0) {
    // Without values, assume true
    return true
  }
  if (truthValues.length === 1) {
    // With a single values, the truth of the set is the truth of the value
    return truthValues[0]
  }

  if (!logic) {
    // If no custom logic...
    return logicalOr
      ? // If using logicalOr, return true if ANY condition is true
        truthValues.some(value => !!value)
      : // Else, whether or not ALL conditions are true
        !truthValues.some(value => !value)
  }

  // Replace each index in the logic string with its boolean value
  let logicStr = logic
  for (let i = truthValues.length - 1; i >= 0; i--) {
    logicStr = logicStr.replaceAll(`${i}`, `${truthValues[i]}`)
  }

  // Safely parse and evaluate the logic string
  return evaluateLogicString(logicStr)
}

// Tokenizes the logic string
export const tokenizeLogicString = (logicStr: string): string[] => {
  const tokens = logicStr.match(/true|false|&&|\|\||\(|\)/g)
  if (!tokens || tokens.length === 0) {
    throw new Error("Invalid logic string.")
  }
  return tokens
}

// Validates tokens and checks for balanced parentheses
export const validateTokens = (tokens: string[]): void => {
  let openParentheses = 0

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]

    if (token === "(") openParentheses++
    else if (token === ")") openParentheses--

    // Consecutive operators or unbalanced parentheses
    if (
      (tokens[i] === "&&" || tokens[i] === "||") &&
      (tokens[i + 1] === "&&" || tokens[i + 1] === "||")
    ) {
      throw new Error("Invalid logic string.")
    }

    if (openParentheses < 0) {
      throw new Error("Invalid logic string.")
    }
  }

  // All parentheses should be balanced
  if (openParentheses !== 0) {
    throw new Error("Invalid logic string.")
  }
}

// Converts infix notation to postfix notation using the Shunting-yard algorithm
export const toPostfix = (tokens: string[]): (string | boolean)[] => {
  type Precedence = { "||": number; "&&": number }
  type Operator = "||" | "&&" | "(" | ")"
  const precedence: Precedence = { "||": 1, "&&": 2 }
  const output: (string | boolean)[] = []
  const operators: Operator[] = []

  const processOperator = (token: "||" | "&&") => {
    while (
      operators.length &&
      precedence[operators[operators.length - 1] as keyof Precedence] >=
      precedence[token]
    ) {
      const op = operators.pop()
      if (op === "||" || op === "&&") output.push(op)
    }
    operators.push(token)
  }

  const processParenthesis = () => {
    while (operators.length && operators[operators.length - 1] !== "(") {
      const op = operators.pop()
      if (op === "||" || op === "&&") output.push(op)
    }
    operators.pop() // Remove the '('
  }

  for (const token of tokens) {
    if (token === "true" || token === "false") {
      output.push(token === "true")
    } else if (token === "&&" || token === "||") {
      processOperator(token)
    } else if (token === "(") {
      operators.push(token)
    } else if (token === ")") {
      processParenthesis()
    }
  }

  while (operators.length) {
    const op = operators.pop()
    if (op === "(" || op === ")") {
      throw new Error("Invalid logic string.")
    }
    if (op) {
      output.push(op)
    }
  }

  return output
}

// Helper function to evaluate a boolean operation
const evaluateBooleanOperation = (
  operator: string,
  a: boolean,
  b: boolean,
): boolean => {
  return operator === "&&" ? a && b : a || b
}

// Helper function to process an operator token
const processOperatorToken = (
  stack: boolean[],
  operator: string,
): boolean[] => {
  if (stack.length < 2) {
    throw new Error("Invalid logic string.")
  }

  const b = stack.pop()
  const a = stack.pop()

  if (a === undefined || b === undefined) {
    throw new Error("Invalid logic string.")
  }

  stack.push(evaluateBooleanOperation(operator, a, b))
  return stack
}

// Evaluates the postfix expression to return a boolean result
export const evaluatePostfix = (postfix: (string | boolean)[]): boolean => {
  const stack: boolean[] = []

  for (const token of postfix) {
    if (typeof token === "boolean") {
      stack.push(token)
      continue
    }

    if (token === "&&" || token === "||") {
      processOperatorToken(stack, token)
    }
  }

  if (stack.length !== 1) {
    throw new Error("Invalid logic string.")
  }

  return stack[0]
}

// Main function: parses and evaluates the logic string
export const evaluateLogicString = (logicStr: string): boolean => {
  const tokens = tokenizeLogicString(logicStr)
  validateTokens(tokens)
  const postfix = toPostfix(tokens)
  return evaluatePostfix(postfix)
}
